/**
 * Tipos do cadastro de Modelos de andamento.
 *
 * Reaproveita a mesma ideia de modelos-documento (texto com variáveis, versões,
 * status ativo/arquivado), mas os modelos são organizados por TIPO DE ANDAMENTO
 * em vez de categoria de documento. Os tipos de andamento NÃO são criados aqui —
 * vêm fixos do domínio do processo.
 */

export type TipoAndamento =
  | 'INFORMATIVO'
  | 'SISTEMA'
  | 'DESPACHO'
  | 'CERTIDAO'
  | 'INTIMACAO'
  | 'CONCLUSO'
  | 'TRIAGEM';

export const TIPO_ANDAMENTO_LABEL: Record<TipoAndamento, string> = {
  INFORMATIVO: 'Informativo',
  SISTEMA: 'Sistema',
  DESPACHO: 'Despacho',
  CERTIDAO: 'Certidão',
  INTIMACAO: 'Intimação',
  CONCLUSO: 'Concluso',
  TRIAGEM: 'Triagem',
};

export const TIPO_ANDAMENTO_DESCRICAO: Record<TipoAndamento, string> = {
  INFORMATIVO: 'Registro informativo no histórico do processo.',
  SISTEMA: 'Andamento gerado automaticamente por uma ação do sistema.',
  DESPACHO: 'Despacho da secretaria ou do árbitro.',
  CERTIDAO: 'Certidão lavrada no processo.',
  INTIMACAO: 'Texto de intimação às partes.',
  CONCLUSO: 'Processo concluso para decisão.',
  TRIAGEM:
    'Andamento único das decisões de triagem (aprovar, pedir ajuste, negar). Sem variáveis: em lote o texto é registrado igual em todos os processos.',
};

export const TIPOS_ANDAMENTO: TipoAndamento[] = [
  'INFORMATIVO',
  'SISTEMA',
  'DESPACHO',
  'CERTIDAO',
  'INTIMACAO',
  'CONCLUSO',
  'TRIAGEM',
];

/** Variáveis disponíveis para interpolar no texto do andamento. */
export interface VariavelAndamento {
  key: string;
  descricao: string;
  exemplo: string;
}

/** DTO de variável retornado por `GET /variaveis` (mapeado para {@link VariavelAndamento} no service). */
export interface VariavelAndamentoApi {
  key: string;
  label: string;
  exemplo: string;
}

export const VARIAVEIS_ANDAMENTO: VariavelAndamento[] = [
  { key: 'requerente', descricao: 'Nome do requerente', exemplo: 'Construtora Aurora Ltda.' },
  { key: 'requerido', descricao: 'Nome do requerido', exemplo: 'João da Silva' },
  { key: 'numero', descricao: 'Número do processo', exemplo: '0001234-56.2026' },
  { key: 'data_hoje', descricao: 'Data de hoje', exemplo: '23/06/2026' },
  { key: 'arbitro', descricao: 'Árbitro responsável', exemplo: 'Kátia Menezes' },
  { key: 'prazo_dias', descricao: 'Prazo em dias úteis', exemplo: '15' },
  // O valor real já traz o "por". Sem meio registrado ele resolve vazio, e o core remove o espaço
  // que sobra antes do ponto: "Citação comprovada {{meio_citacao}}." vira "Citação comprovada.".
  {
    key: 'meio_citacao',
    descricao: 'Meio que comprovou a citação (já com "por")',
    exemplo: 'por clique no link do e-mail',
  },
];

export interface ModeloAndamentoVersao {
  versao: number;
  texto: string;
  autor: string;
  criadoEm: string;
}

export interface ModeloAndamento {
  id: string;
  tipo: TipoAndamento;
  nome: string;
  descricao: string | null;
  texto: string;
  ativo: boolean;
  versaoAtual: number;
  versoes: ModeloAndamentoVersao[];
  criadoEm: string;
  atualizadoEm: string;
}

/** Versão enxuta usada pelo seletor do bloco "Criar andamento". */
export interface ModeloAndamentoOption {
  id: string;
  tipo: TipoAndamento;
  nome: string;
  texto: string;
}

export interface CreateModeloAndamentoDto {
  tipo: TipoAndamento;
  nome: string;
  descricao?: string | null;
  texto: string;
}

export interface UpdateModeloAndamentoDto {
  nome?: string;
  descricao?: string | null;
  texto?: string;
}

export type StatusFiltroModeloAndamento = 'todos' | 'ativos' | 'arquivados';

export interface ModelosAndamentoFilters {
  search: string;
  tipo: TipoAndamento | 'todos';
  status: StatusFiltroModeloAndamento;
}

export interface ModelosAndamentoState {
  modelos: ModeloAndamento[];
  variaveis: VariavelAndamento[];
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}
